import React from "react";

type Stat = { label: string; value: string | number };
type Section = { name: string; progress: number; status?: string };

export type PersonalizeOverviewCardProps = {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  progress?: number;               // 0-100
  stats?: Stat[];
  sections?: Section[];            // for surveys
  ctaLabel: string;
  onClick: () => void;
  highlight?: boolean;             // use brand emphasis (e.g., Surveys)
};

export default function PersonalizeOverviewCard({
  icon, title, subtitle, progress, stats, sections, ctaLabel, onClick, highlight
}: PersonalizeOverviewCardProps) {
  return (
    <div className={[
      "group relative rounded-xl bg-white ring-1 ring-slate-200 shadow-sm p-4 md:p-5",
      highlight ? "ring-[#18365E] shadow-md" : ""
    ].join(" ")}>
      <div className="flex items-center gap-3">
        {icon && <div className="text-[#18365E]">{icon}</div>}
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-[#0A2540] truncate">{title}</h3>
          {subtitle && <p className="text-[13px] text-slate-600 truncate">{subtitle}</p>}
        </div>
      </div>

      {typeof progress === "number" && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[12px] text-slate-600 mb-1">
            <span>Progress</span><span>{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-2 rounded-full bg-[#18365E] transition-all"
              style={{ width: `${Math.min(Math.max(progress,0),100)}%` }}
            />
          </div>
        </div>
      )}

      {stats && stats.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-3 text-[12px] text-slate-600">
          {stats.map((s, i) => (
            <div key={i} className="truncate">
              <div className="text-slate-500">{s.label}</div>
              <div className="font-medium text-slate-700 truncate">{s.value ?? "—"}</div>
            </div>
          ))}
        </div>
      )}

      {sections && sections.length > 0 && (
        <div className="mt-3 space-y-2">
          {sections.map((sec, i) => (
            <div key={i}>
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-slate-700">{sec.name}</span>
                <span className="text-slate-600">{sec.progress}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-1.5 bg-[#18365E]"
                  style={{ width: `${sec.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <button
          onClick={onClick}
          className="w-full md:w-auto inline-flex items-center justify-center rounded-lg px-3.5 py-2 text-[13px] font-medium text-white bg-[#18365E] hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6E4C] transition"
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}